import { confirmUserAttribute, updateMFAPreference, updateUserAttribute, verifyTOTPSetup } from "aws-amplify/auth";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  SelectMultifactorAuthenticationForm,
  SelectMultifactorAuthenticationFormData,
  SetupSMSForm,
  ThemedAnchor,
  ThemedButton,
  VerifyCodeForm,
} from "@/App/components";
import { SetupTOTPForm } from "@/App/components/MultifactorAuthentication/SetupTOTPForm";
import { AuthenticationContext, MultifactorAuthenticationContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { enableSmsMultifactorAuthentication } from "@/App/utils/featureFlags";

type MulitfactorAuthenticationPageStep = "selectMethod" | "setupTotp" | "setupSms" | "verifyTotp" | "verifySms";

/**
 * This page is intended to only be summoned when the member
 * is forced to setup and use MFA. If you're looking for the page
 * that allows members to enter MFA in the usual login flow, check
 * the LoginMultifactorAuthenticationPage
 *
 * There's some considerable overlap between the two but rather than
 * abstract the functionality into oblivion i've elected to keep them
 * separate.
 */
export const ForceMultifactorAuthenticationPage: FC = () => {
  const { t: translate } = useTranslation();
  const [step, setStep] = useState<MulitfactorAuthenticationPageStep>("selectMethod");
  const { setEnforceMfaSetup, enforceMfaSetup } = useContext(MultifactorAuthenticationContext);
  const { logoutUser, authState } = useContext(AuthenticationContext);
  const navigate = useNavigateClient();
  const alert = useAlert();

  // if they shouldn't be here, they shouldn't be here
  useEffect(() => {
    if (!enforceMfaSetup) {
      if (authState === "signedIn") {
        navigate("/");
      } else {
        navigate("/login");
      }
    } else if (authState !== "signedIn") {
      navigate("/login");
    }
  }, [authState, enforceMfaSetup]);

  const onSelectMultifactorAuth = async (formData: SelectMultifactorAuthenticationFormData) => {
    const { multifactorAuthenticationMode } = formData;
    if (multifactorAuthenticationMode === "totp") {
      setStep("setupTotp");
    } else if (multifactorAuthenticationMode === "sms") {
      setStep("setupSms");
    }
  };

  const onTotpSetup = async () => {
    setStep("verifyTotp");
  };

  const onSmsSetup = async (phoneNumber: string) => {
    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: "phone_number",
          value: phoneNumber,
        },
      });
      setStep("verifySms");
    } catch {
      alert({
        message: translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.problemSettingPhoneNumber"),
        type: "error",
        style: "toast",
      });
    }
  };

  const verifyTotpCode = async (code: string): Promise<void> => {
    try {
      await verifyTOTPSetup({
        code: code,
      });
    } catch {
      throw new Error(translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.unableToVerifyAuthenticatorCode"));
    }

    try {
      await updateMFAPreference({
        totp: "PREFERRED",
      });
    } catch {
      // don't do anything here, this isn't really a blocking error
    }
  };

  const verifySmsCode = async (code: string): Promise<void> => {
    try {
      await confirmUserAttribute({
        userAttributeKey: "phone_number",
        confirmationCode: code,
      });
    } catch {
      throw new Error(translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.unableToVerifyPhoneNumber"));
    }

    try {
      await updateMFAPreference({
        sms: "PREFERRED",
      });
    } catch {
      // don't do anything here, this isn't really a blocking error
    }
  };

  const onVerifyCode = async (fullCode: string) => {
    let verified = false;
    if (step === "verifyTotp") {
      await verifyTotpCode(fullCode).then(() => {
        verified = true;
      });
    } else if (step === "verifySms") {
      await verifySmsCode(fullCode).then(() => {
        verified = true;
      });
    }

    if (verified) {
      setEnforceMfaSetup(false);
      alert({
        message: translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.multifactorAuthenticationSetupSuccessful"),
        type: "success",
        style: "toast",
      });
      navigate("/");
    }
  };

  const onBackToLogin = useCallback(async () => {
    try {
      await logoutUser();
    } catch (e) {
      console.error(e);
    }
    navigate("/login");
  }, [logoutUser]);

  const onChangeVerificationMethod = useCallback(() => {
    setStep("selectMethod");
  }, []);

  return (
    <Stack spacing="medium-large">
      <Stack>
        {step === "selectMethod" && (
          <Subheading textAlign="center">
            {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.selectYourVerificationMethod")}
          </Subheading>
        )}
        {step === "verifyTotp" && (
          <Subheading textAlign="center">
            {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.enterVerificationCode")}
          </Subheading>
        )}
        {step === "setupTotp" && (
          <Subheading textAlign="center">
            {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.setUpOnAuthenticatorApp")}
          </Subheading>
        )}
        <Body textAlign="center">
          {step === "selectMethod" && <>{translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.keepYourAccountSafe")}</>}
          {step === "setupTotp" && <>{translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.scanTheQrCode")}</>}
          {step === "setupSms" && <>{translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.enterYourMobileNumber")}</>}
          {step === "verifyTotp" && <>{translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.enterTheCodeAuthenticatorApp")}</>}
          {step === "verifySms" && <>{translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.enterTheCodeMobile")}</>}
        </Body>
        {step === "selectMethod" && <SelectMultifactorAuthenticationForm onSubmit={onSelectMultifactorAuth} />}
        {step === "setupTotp" && <SetupTOTPForm onSubmit={onTotpSetup} />}
        {step === "setupSms" && <SetupSMSForm onSubmit={onSmsSetup} />}
        {["verifyTotp", "verifySms"].includes(step) && <VerifyCodeForm handleVerifyCode={onVerifyCode} />}

        {step !== "selectMethod" && enableSmsMultifactorAuthentication() && (
          <ThemedButton appearance="secondary" onClick={onChangeVerificationMethod}>
            {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.chooseDifferentVerificationMethod")}
          </ThemedButton>
        )}
      </Stack>
      <Stack>
        <ThemedButton appearance="text" onClick={() => onBackToLogin()}>
          {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.backToLogin")}
        </ThemedButton>
        <Body textAlign="center">
          {translate("FORCE_MULTIFACTOR_AUTHENTICATION_PAGE.havingProblems")}<ThemedAnchor href="tel:(888) 832-2779">(888) 832-2779</ThemedAnchor>.
        </Body>
      </Stack>
    </Stack>
  );
};
